"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { CreditCard, Bell, UserCog } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import useClientTranslation from "@/hooks/useClientTranslation";
import { cn } from "@/lib/utils";

interface MenuItem {
  id: string;
  nameKey: string;
  icon: React.ReactNode;
  href: string;
}

export default function Sidebar() {
  const pathname = usePathname();
  const { t } = useClientTranslation("pim");

  const menus: MenuItem[] = [
    { id: "profile", nameKey: "sidebar.profile", icon: <UserCog size={16} />, href: "/pim" },
    { id: "recharge", nameKey: "sidebar.recharge", icon: <CreditCard size={16} />, href: "/pim/recharge" },
    { id: "notify", nameKey: "sidebar.notify", icon: <Bell size={16} />, href: "/pim/notify" },
  ];

  const isActive = (href: string) => {
    if (href === "/pim") {
      return pathname === "/pim";
    }
    return pathname.startsWith(href);
  };

  return (
    <div className="sticky top-16 h-[calc(100vh-4rem)] w-56 flex flex-col border-r border-border bg-card">
      <div className="px-5 py-4 flex-shrink-0">
        <h2 className="text-xl font-semibold">{t("sidebar.profile")}</h2>
      </div>
      <Separator />

      <div className="flex-1 overflow-y-auto py-2">
        <div className="space-y-0.5 px-2">
          {menus.map(menu => (
            <Link
              key={menu.id}
              href={menu.href}
              className={cn(
                "w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                isActive(menu.href)
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-foreground hover:bg-muted"
              )}
            >
              {menu.icon}
              <span>{t(menu.nameKey)}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
